import { IconButton, Popover, Button, Typography, Box } from "@mui/material";
import { Stack } from "@mui/system";
import CheckIcon from "@mui/icons-material/Check";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  colors,
  spacing,
  typography,
} from "../../../../../../../../../assets/styles/tokens";
import { useState, useMemo, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setFilteredRecordsForBudgetMobile,
  setHasDataFetchedForBudgetMobile,
  setSelectedBrandForBudget,
  setSelectedEndDateForBudget,
  setSelectedStartDateForBudget,
} from "../../../../../../../../../features/businessLeaderDashboardSlice";
import { useSearchParams } from "react-router-dom";
import { saveAs } from "file-saver";
import { additionalQueriestableHeadings04 } from "../../../../../AdditionalQueries/AdditionalQueriesAccordion/AdditionalQueriesConstants";

export type DateRange = {
  label: string;
  displayRange: string;
  startDate: Date;
  endDate: Date;
  isCurrent: boolean;
};

const midnight = (d: Date) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate());

const daysAgo = (n: number): Date => {
  const d = midnight(new Date());
  d.setDate(d.getDate() - n);
  return d;
};

const lastDayOfMonth = (year: number, month: number): Date =>
  new Date(year, month + 1, 0);

const lastDayOfQuarter = (year: number, firstMonth: number): Date =>
  lastDayOfMonth(year, firstMonth + 2);

const fmt = (d: Date): string =>
  `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`;

const formatDate = (date: Date | string): string => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const HOW_MANY_MONTHS = 24;
const HOW_MANY_QUARTERS = 12;

const generateMonthOptions = (): DateRange[] => {
  const today = new Date();
  const limitDate = daysAgo(2);
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth();
  const options: DateRange[] = [];
  for (let i = 0; i < HOW_MANY_MONTHS; i++) {
    let month = currentMonth - i;
    let year = currentYear;
    while (month < 0) {
      month += 12;
      year -= 1;
    }
    const startDate = new Date(year, month, 1);
    const originalEndDate = lastDayOfMonth(year, month);
    const endDate = originalEndDate > limitDate ? limitDate : originalEndDate;
    const isCurrent = i === 0;
    const monthName = startDate.toLocaleString("en-US", { month: "short" });
    const label = `${monthName} ${year}`;
    const displayRange = `${fmt(startDate)}-${fmt(endDate)}`;
    options.push({ label, displayRange, startDate, endDate, isCurrent });
  }
  return options;
};

const generateQuarterOptions = (): DateRange[] => {
  const today = new Date();
  const limitDate = daysAgo(2);
  const currentYear = today.getFullYear();
  const currentQuarter = Math.floor(today.getMonth() / 3);
  const options: DateRange[] = [];
  for (let i = 0; i < HOW_MANY_QUARTERS; i++) {
    let quarter = currentQuarter - i;
    let year = currentYear;
    while (quarter < 0) {
      quarter += 4;
      year -= 1;
    }
    const firstMonth = quarter * 3;
    const startDate = new Date(year, firstMonth, 1);
    const originalEndDate = lastDayOfQuarter(year, firstMonth);
    const endDate = originalEndDate > limitDate ? limitDate : originalEndDate;
    const isCurrent = i === 0;
    const label = `Q${quarter + 1} ${year}`;
    const displayRange = `${fmt(startDate)}-${fmt(endDate)}`;
    options.push({ label, displayRange, startDate, endDate, isCurrent });
  }
  return options;
};

export type BudgetDateFilterProps = {
  onChange?: (range: DateRange, mode: "Month" | "Quarter") => void;
};

const BudgetDateFilter = ({ onChange }: BudgetDateFilterProps) => {
  const dispatch = useDispatch();
  const [, setSearchParams] = useSearchParams();

  const monthOptions = useMemo(generateMonthOptions, []);
  const quarterOptions = useMemo(generateQuarterOptions, []);

  const headCells = Object.entries(additionalQueriestableHeadings04);
  const { filteredRecordsForBudgetMobile, isLoadingMobileBizLeader } =
    useSelector((state: any) => state.businessLeaderDashBoard);

  // ── Popover open/close ────────────────────────────────────────────────────
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  // ── popoverTab: only controls what's shown INSIDE the popover ────────────
  // Does NOT affect the button label or trigger any API
  const [popoverTab, setPopoverTab] = useState<"Month" | "Quarter">(() => {
    const mode = new URLSearchParams(window.location.search).get(
      "modeForBudget",
    );
    return mode === "Quarter" ? "Quarter" : "Month";
  });

  // ── appliedSelection: what is ACTUALLY applied (drives button label + API)
  // This only changes when user explicitly picks an option from the list
  const [appliedSelection, setAppliedSelection] = useState<{
    option: DateRange;
    mode: "Month" | "Quarter";
  }>(() => {
    const startParam = new URLSearchParams(window.location.search).get(
      "startDateForBudget",
    );
    const modeParam = new URLSearchParams(window.location.search).get(
      "modeForBudget",
    );

    if (modeParam === "Quarter" && startParam) {
      const matched = generateQuarterOptions().find(
        (o) => formatDate(o.startDate) === startParam,
      );
      if (matched) return { option: matched, mode: "Quarter" };
    }

    if (startParam) {
      const matched = generateMonthOptions().find(
        (o) => formatDate(o.startDate) === startParam,
      );
      if (matched) return { option: matched, mode: "Month" };
    }

    return { option: generateMonthOptions()[0], mode: "Month" };
  });

  const tabRef = useRef(popoverTab);

  // ── On mount: sync Redux from URL or set defaults ─────────────────────────
  useEffect(() => {
    const startParam = new URLSearchParams(window.location.search).get(
      "startDateForBudget",
    );
    const endParam = new URLSearchParams(window.location.search).get(
      "endDateForBudget",
    );
    const modeParam = new URLSearchParams(window.location.search).get(
      "modeForBudget",
    );

    if (startParam && endParam && modeParam) {
      dispatch(setSelectedStartDateForBudget(startParam));
      dispatch(setSelectedEndDateForBudget(endParam));
    } else {
      const defaultOption = monthOptions[0];
      dispatch(
        setSelectedStartDateForBudget(formatDate(defaultOption.startDate)),
      );
      dispatch(setSelectedEndDateForBudget(formatDate(defaultOption.endDate)));
      setSearchParams(
        (prev) => {
          prev.set("startDateForBudget", formatDate(defaultOption.startDate));
          prev.set("endDateForBudget", formatDate(defaultOption.endDate));
          prev.set("modeForBudget", "Month");
          return prev;
        },
        { replace: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Button label always shows appliedSelection — never changes on tab switch
  const displayLabel = `${appliedSelection.option.label} (${appliedSelection.option.displayRange})`;

  const currentOptions = popoverTab === "Month" ? monthOptions : quarterOptions;

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    // Sync popoverTab to applied mode when opening
    setPopoverTab(appliedSelection.mode);
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  // Tab switch inside popover — ONLY changes what list is shown
  // NO API call, NO button label change, NO URL change
  const handleTabChange = (newTab: "Month" | "Quarter") => {
    if (newTab === popoverTab) return;
    tabRef.current = newTab;
    setPopoverTab(newTab);
  };

  // User explicitly picks an option — ONLY time API fires
  const handleSelect = (option: DateRange) => {
    // Same option already applied — do nothing
    if (
      option.label === appliedSelection.option.label &&
      popoverTab === appliedSelection.mode
    ) {
      handleClose();
      return;
    }

    const startStr = formatDate(option.startDate);
    const endStr = formatDate(option.endDate);

    // Update applied selection
    setAppliedSelection({ option, mode: popoverTab });

    dispatch(setHasDataFetchedForBudgetMobile(false));
    dispatch(setSelectedStartDateForBudget(startStr));
    dispatch(setSelectedEndDateForBudget(endStr));
    dispatch(setSelectedBrandForBudget("")); // reset brand filter on date change
    dispatch(setFilteredRecordsForBudgetMobile([])); // clear filtered records on date change

    setSearchParams(
      (prev) => {
        prev.set("startDateForBudget", startStr);
        prev.set("endDateForBudget", endStr);
        prev.set("modeForBudget", popoverTab);
        prev.delete("searchBrandForBudget");
        return prev;
      },
      { replace: true },
    );

    onChange?.(option, popoverTab);
    handleClose();
  };

  // ── CSV Export ────────────────────────────────────────────────────────────
  const handleExportCSV = () => {
    const csvRows = [];
    const headers = headCells.map((pair) => pair[0]) as any;
    csvRows.push(headers.join(","));
    // eslint-disable-next-line
    filteredRecordsForBudgetMobile.map((row: any) => {
      let rowData = [] as any;
      rowData.push(row?.brand_id);
      rowData.push(row?.brand);
      rowData.push(Math.round(row?.qftd));
      rowData.push(Math.round(row?.qftd_budget));
      rowData.push(row?.perc_Qftd_budget_use);
      rowData.push(Math.round(row?.Qftd_RR));
      rowData.push(row?.perc_of_RR_qftd_budget_use);
      csvRows.push(rowData.join(","));
    });
    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "QFTDBudgetControl.csv");
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
       sx={{
          mt: spacing.Spacing.xs.$value,
          mb: spacing.Spacing.lg.$value,
        }}
      >
        <Button
          data-tracker="658"
          disabled={isLoadingMobileBizLeader}
          onClick={handleOpen}
          endIcon={<ArrowDropDownIcon />}
          size="small"
          sx={{
            width: "61%",
            justifyContent: "space-between",
            fontSize: typography.Size.Text.sm.$value,
            color: colors.GrayLightMode[900].$value,
            border: `1px solid ${colors.GrayLightMode[300].$value}`,
            borderRadius: spacing.Radius.md.$value,
            padding: `${spacing.Spacing.xs.$value} ${spacing.Spacing.md.$value}`,
            textTransform: "none",
            backgroundColor: "#fff",
            "&:hover": { backgroundColor: colors.GrayLightMode[50].$value },
          }}
        >
          {displayLabel}
        </Button>

        <IconButton
          color="secondary"
          size="large"
          onClick={handleExportCSV}
          data-tracker="659"
          disabled={
            isLoadingMobileBizLeader ||
            filteredRecordsForBudgetMobile?.length === 0
          }
        >
          <FileDownloadIcon sx={{ color: colors.GrayLightMode[700].$value }} />
        </IconButton>
      </Stack>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 0.5,
            width: 340,
            borderRadius: spacing.Radius.md.$value,
            boxShadow: `0px 4px 12px rgba(0, 0, 0, 0.1)`,
            border: `1px solid ${colors.GrayLightMode[200].$value}`,
          },
        }}
      >
        {/* Month / Quarter toggle — only affects popover list, not button label */}
        <Box
          sx={{
            display: "flex",
            gap: "2px",
            backgroundColor: colors.GrayLightMode[100].$value,
            borderRadius: spacing.Radius.sm.$value,
            padding: "3px",
            margin: `${spacing.Spacing.sm.$value} ${spacing.Spacing.sm.$value} 0`,
          }}
        >
          {(["Month", "Quarter"] as const).map((t) => (
            <Box
              key={t}
              component="button"
              onClick={() => handleTabChange(t)}
              sx={{
                flex: 1,
                fontSize: typography.Size.Text.sm.$value,
                fontWeight: 500,
                borderRadius: spacing.Radius.sm.$value,
                border: "none",
                cursor: "pointer",
                py: "5px",
                transition: "background 0.15s, color 0.15s, box-shadow 0.15s",
                backgroundColor: popoverTab === t ? "#fff" : "transparent",
                color:
                  popoverTab === t
                    ? colors.GrayLightMode[900].$value
                    : colors.GrayLightMode[500].$value,
                boxShadow:
                  popoverTab === t ? "0 1px 3px rgba(0,0,0,0.10)" : "none",
                "&:hover": {
                  backgroundColor:
                    popoverTab === t
                      ? "#fff"
                      : colors.GrayLightMode[200].$value,
                },
              }}
            >
              {t}
            </Box>
          ))}
        </Box>

        {/* Scrollable option list */}
        <Box sx={{ maxHeight: 260, overflowY: "auto", py: "6px" }}>
          {currentOptions.map((option) => {
            //Highlight only if this option matches the APPLIED selection
            // AND we're on the same mode tab
            const isSelected =
              option.label === appliedSelection.option.label &&
              popoverTab === appliedSelection.mode;

            return (
              <Box
                key={option.label}
                onClick={() => handleSelect(option)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  pl: spacing.Spacing["5xl"].$value,
                  pr: spacing.Spacing.md.$value,
                  py: "10px",
                  position: "relative",
                  cursor: "pointer",
                  backgroundColor: isSelected
                    ? colors.Brand[50].$value
                    : "transparent",
                  "&:hover": {
                    backgroundColor: isSelected
                      ? colors.Brand[50].$value
                      : colors.GrayLightMode[50].$value,
                  },
                }}
              >
                {isSelected && (
                  <CheckIcon
                    fontSize="small"
                    sx={{
                      position: "absolute",
                      left: spacing.Spacing.lg.$value,
                      color: colors.GrayLightMode[900].$value,
                      fontSize: "16px",
                    }}
                  />
                )}
                <Typography
                  sx={{
                    fontSize: typography.Size.Text.sm.$value,
                    color: colors.GrayLightMode[900].$value,
                    fontWeight: isSelected ? 500 : 400,
                  }}
                >
                  {option.label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Popover>
    </>
  );
};

export default BudgetDateFilter;
